import { PencilSquareIcon, TrashIcon, UserCircleIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'

import { MockNote, MOCK_NOTES } from './mockData'
import { useProviderSession } from './previewSession'

function formatDateTime(value?: string | null) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.valueOf())) return null
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// Internal coordinator notes for one family. Org-only record-keeping: nothing
// here is ever shown in the family app.
export default function FamilyNotesSection({ licenseId }: { licenseId: string }) {
    const session = useProviderSession()
    // Viewer is read-only: no composer, no delete. Edit is already author-only.
    const canWrite = session?.role !== 'viewer'
    const [notes, setNotes] = useState<MockNote[]>(MOCK_NOTES[licenseId] ?? [])
    const [draft, setDraft] = useState('')

    function handleAdd() {
        if (!draft.trim()) return
        const newNote: MockNote = {
            _id: `note_new_${Date.now()}`,
            body: draft.trim(),
            authorName: session?.user.name ?? 'You',
            isOwn: true,
            createdAt: new Date().toISOString(),
            edited: false,
        }
        setNotes((prev) => [newNote, ...prev])
        setDraft('')
    }

    function handleUpdated(updated: MockNote) {
        setNotes((prev) => prev.map((n) => (n._id === updated._id ? updated : n)))
    }

    function handleDeleted(noteId: string) {
        setNotes((prev) => prev.filter((n) => n._id !== noteId))
    }

    return (
        <section className='bg-white border border-neutral-200 rounded-2xl p-6 mt-6'>
            <h2 className='text-lg font-semibold text-neutral-900 mb-1'>Coordinator notes</h2>
            <p className='text-sm text-neutral-600 mb-4'>Internal to your organization. The client never sees these.</p>

            {canWrite && (
                <div className='mb-5'>
                    <textarea
                        rows={3}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        maxLength={5000}
                        placeholder='Add a note about this client…'
                        className='w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm'
                    />
                    <button
                        onClick={handleAdd}
                        disabled={!draft.trim()}
                        className='mt-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50'
                    >
                        Add note
                    </button>
                </div>
            )}

            {notes.length === 0 ? (
                <div className='text-sm text-neutral-500'>No notes yet.</div>
            ) : (
                <ul className='space-y-3'>
                    {notes.map((note) => (
                        <NoteRow
                            key={note._id}
                            note={note}
                            canWrite={canWrite}
                            onUpdated={handleUpdated}
                            onDeleted={handleDeleted}
                        />
                    ))}
                </ul>
            )}
        </section>
    )
}

function NoteRow({
    note,
    canWrite,
    onUpdated,
    onDeleted,
}: {
    note: MockNote
    canWrite: boolean
    onUpdated: (note: MockNote) => void
    onDeleted: (noteId: string) => void
}) {
    const [editing, setEditing] = useState(false)
    const [editDraft, setEditDraft] = useState(note.body)
    const [deleteState, setDeleteState] = useState<'idle' | 'confirm'>('idle')

    function handleSave() {
        if (!editDraft.trim()) return
        onUpdated({ ...note, body: editDraft.trim(), edited: true })
        setEditing(false)
    }

    return (
        <li className='border border-neutral-200 rounded-xl p-4'>
            <div className='flex items-center justify-between gap-2 mb-2'>
                <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-500'>
                    <span className='inline-flex items-center gap-1'>
                        <UserCircleIcon className='w-3.5 h-3.5' />
                        <span className='font-medium text-neutral-700'>{note.authorName ?? 'Unknown'}</span>
                    </span>
                    <span>·</span>
                    <span>{formatDateTime(note.createdAt)}</span>
                    {note.edited && (
                        <>
                            <span>·</span>
                            <span className='italic'>edited</span>
                        </>
                    )}
                </div>
                {!editing && canWrite && (
                    <div className='flex items-center gap-1 flex-shrink-0'>
                        {note.isOwn && (
                            <button
                                onClick={() => {
                                    setEditDraft(note.body)
                                    setEditing(true)
                                }}
                                title='Edit note'
                                className='p-1.5 text-neutral-400 hover:text-brand-700 hover:bg-brand-50 rounded-lg'
                            >
                                <PencilSquareIcon className='w-4 h-4' />
                            </button>
                        )}
                        {deleteState === 'confirm' ? (
                            <span className='flex items-center gap-1.5'>
                                <button
                                    onClick={() => onDeleted(note._id)}
                                    className='px-2 py-1 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg'
                                >
                                    Confirm delete
                                </button>
                                <button
                                    onClick={() => setDeleteState('idle')}
                                    className='px-2 py-1 text-xs font-medium text-neutral-600 hover:bg-neutral-100 rounded-lg'
                                >
                                    Keep
                                </button>
                            </span>
                        ) : (
                            <button
                                onClick={() => setDeleteState('confirm')}
                                title='Delete note'
                                className='p-1.5 text-neutral-400 hover:text-red-700 hover:bg-red-50 rounded-lg'
                            >
                                <TrashIcon className='w-4 h-4' />
                            </button>
                        )}
                    </div>
                )}
            </div>

            {editing ? (
                <div>
                    <textarea
                        rows={3}
                        value={editDraft}
                        onChange={(e) => setEditDraft(e.target.value)}
                        maxLength={5000}
                        className='w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm'
                    />
                    <div className='mt-2 flex items-center gap-2'>
                        <button
                            onClick={handleSave}
                            disabled={!editDraft.trim()}
                            className='px-3 py-1.5 text-xs font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50'
                        >
                            Save
                        </button>
                        <button
                            onClick={() => setEditing(false)}
                            className='px-3 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-100 rounded-lg'
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            ) : (
                <p className='text-sm text-neutral-800 whitespace-pre-wrap'>{note.body}</p>
            )}
        </li>
    )
}
